
/* ========================================================================== */
/* Product Specs - Figures Gallery
   Location: snippets/product-specs_figures-gallery.liquid
   CSS: assets/custom-product.css - search "Product Specs - Figures Gallery"
   Purpose: Bottom arrow + page-aware progress thumb for blind box figures gallery
========================================================================== */

(function () {
  const SELECTORS = {
    root: "[data-product-specs-figures-gallery]",
    track: "[data-product-specs-figures-gallery-track]",
    previous: "[data-product-specs-figures-gallery-prev]",
    next: "[data-product-specs-figures-gallery-next]",
    progressFill: "[data-product-specs-figures-gallery-progress-fill]",
    card: "[data-product-specs-figures-gallery-card]"
  };

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getMaxScroll(track) {
    return Math.max(track.scrollWidth - track.clientWidth, 0);
  }

  function getCardStep(track) {
    const card = track.querySelector(SELECTORS.card);
    const gap = parseFloat(window.getComputedStyle(track).columnGap || "0") || 0;
    const cardWidth = card ? card.getBoundingClientRect().width : 180;

    return cardWidth + gap;
  }

  function getPageDistance(track) {
    const cardStep = getCardStep(track);

    if (!cardStep) return 0;

    const visibleCards = Math.max(track.clientWidth / cardStep, 1);
    const cardsToMove = Math.max(Math.floor(visibleCards), 2);

    return cardsToMove * cardStep;
  }

  function updateGalleryProgress(root, track, previous, next, progressFill) {
    const maxScroll = getMaxScroll(track);
    const hasOverflow = maxScroll > 2;
    const currentScroll = clamp(track.scrollLeft, 0, maxScroll);
    const atStart = currentScroll <= 2;
    const atEnd = currentScroll >= maxScroll - 2;

    root.classList.toggle("product-specs-figures-gallery--has-overflow", hasOverflow);

    if (previous) {
      previous.disabled = !hasOverflow || atStart;
    }

    if (next) {
      next.disabled = !hasOverflow || atEnd;
    }

    if (!progressFill) return;

    if (!hasOverflow) {
      progressFill.style.setProperty("--psfg-progress-width", "100%");
      progressFill.style.setProperty("--psfg-progress-left", "0%");
      return;
    }

    const thumbWidthPercent = clamp((track.clientWidth / track.scrollWidth) * 100, 10, 100);
    const maxThumbLeftPercent = 100 - thumbWidthPercent;
    const scrollProgress = maxScroll > 0 ? currentScroll / maxScroll : 0;
    const thumbLeftPercent = scrollProgress * maxThumbLeftPercent;

    progressFill.style.setProperty("--psfg-progress-width", `${thumbWidthPercent}%`);
    progressFill.style.setProperty("--psfg-progress-left", `${thumbLeftPercent}%`);
  }

  function scrollGallery(root, track, previous, next, progressFill, direction) {
    const maxScroll = getMaxScroll(track);
    const pageDistance = getPageDistance(track);

    if (!pageDistance || !maxScroll) return;

    const targetLeft = clamp(
      track.scrollLeft + direction * pageDistance,
      0,
      maxScroll
    );

    track.scrollTo({
      left: targetLeft,
      behavior: "auto"
    });

    updateGalleryProgress(root, track, previous, next, progressFill);
  }

  function jumpGalleryTo(root, track, previous, next, progressFill, position) {
    const maxScroll = getMaxScroll(track);

    if (!maxScroll) return;

    const targetLeft = position === "end" ? maxScroll : 0;

    track.scrollTo({
      left: targetLeft,
      behavior: "auto"
    });

    updateGalleryProgress(root, track, previous, next, progressFill);
  }

  function handleGalleryKeydown(event, root, track, previous, next, progressFill) {
    const maxScroll = getMaxScroll(track);

    if (!maxScroll) return;

    if (event.key === "ArrowRight") {
      event.preventDefault();
      scrollGallery(root, track, previous, next, progressFill, 1);
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      scrollGallery(root, track, previous, next, progressFill, -1);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      jumpGalleryTo(root, track, previous, next, progressFill, "start");
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      jumpGalleryTo(root, track, previous, next, progressFill, "end");
    }
  }

  function initProductSpecsFiguresGallery(root) {
    if (!root || root.dataset.productSpecsFiguresGalleryInitialised === "true") return;

    const track = root.querySelector(SELECTORS.track);
    const previous = root.querySelector(SELECTORS.previous);
    const next = root.querySelector(SELECTORS.next);
    const progressFill = root.querySelector(SELECTORS.progressFill);

    if (!track || !previous || !next) return;

    root.dataset.productSpecsFiguresGalleryInitialised = "true";
    root.classList.add("product-specs-figures-gallery--is-ready");

    previous.addEventListener("click", function () {
      scrollGallery(root, track, previous, next, progressFill, -1);
    });

    next.addEventListener("click", function () {
      scrollGallery(root, track, previous, next, progressFill, 1);
    });

    track.addEventListener("keydown", function (event) {
      handleGalleryKeydown(event, root, track, previous, next, progressFill);
    });

    track.addEventListener("scroll", function () {
      window.requestAnimationFrame(function () {
        updateGalleryProgress(root, track, previous, next, progressFill);
      });
    }, { passive: true });

    window.addEventListener("resize", function () {
      window.requestAnimationFrame(function () {
        updateGalleryProgress(root, track, previous, next, progressFill);
      });
    });

    requestAnimationFrame(function () {
      updateGalleryProgress(root, track, previous, next, progressFill);
    });
  }

  function bootProductSpecsFiguresGallery(scope) {
    const root = scope || document;
    const galleries = root.querySelectorAll(SELECTORS.root);

    galleries.forEach(initProductSpecsFiguresGallery);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      bootProductSpecsFiguresGallery(document);
    }, { once: true });
  } else {
    bootProductSpecsFiguresGallery(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    bootProductSpecsFiguresGallery(event.target);
  });
})();



/* ============================================================================
   Collection Banner V2 Compact
   Section: sections/collection-banner-v2-compact.liquid
============================================================================ */

(function () {
  "use strict";

  if (window.__collectionBannerV2CompactLoaded) return;
  window.__collectionBannerV2CompactLoaded = true;

  const SELECTORS = {
    root: "[data-collection-intro]",
    description: "[data-collection-intro-description]",
    toggle: "[data-collection-intro-toggle]",
    toggleText: "[data-collection-intro-toggle-text]",
    focusable: "a[href], button, input, select, textarea, summary, [tabindex]"
  };

  const COLLAPSED_LINES = 2;
  const OVERFLOW_TOLERANCE = 3;

  function getLineHeight(element) {
    const computed = window.getComputedStyle(element);
    const lineHeight = parseFloat(computed.lineHeight);

    if (!Number.isNaN(lineHeight)) return lineHeight;

    const fontSize = parseFloat(computed.fontSize);
    return Number.isNaN(fontSize) ? 0 : fontSize * 1.36;
  }

  function getFocusableItems(description) {
    return Array.from(description.querySelectorAll(SELECTORS.focusable));
  }

  function restoreOriginalTabIndex(item) {
    const originalTabIndex = item.getAttribute("data-collection-intro-original-tabindex");

    if (originalTabIndex === "__none__") {
      item.removeAttribute("tabindex");
    } else if (originalTabIndex !== null) {
      item.setAttribute("tabindex", originalTabIndex);
    }
  }

  function rectsOverlap(first, second) {
    return (
      first.right > second.left &&
      first.left < second.right &&
      first.bottom > second.top &&
      first.top < second.bottom
    );
  }

  function isFocusableVisible(root, description, item) {
    const descriptionRect = description.getBoundingClientRect();
    const toggle = root.querySelector(SELECTORS.toggle);
    const toggleRect = toggle && !toggle.hidden ? toggle.getBoundingClientRect() : null;

    return Array.from(item.getClientRects()).some(function (itemRect) {
      if (!itemRect.width || !itemRect.height || !rectsOverlap(itemRect, descriptionRect)) {
        return false;
      }

      return !toggleRect || !rectsOverlap(itemRect, toggleRect);
    });
  }

  function setDescriptionFocusables(root, description, expanded) {
    getFocusableItems(description).forEach(function (item) {
      if (!item.hasAttribute("data-collection-intro-original-tabindex")) {
        const originalTabIndex = item.getAttribute("tabindex");
        item.setAttribute(
          "data-collection-intro-original-tabindex",
          originalTabIndex === null ? "__none__" : originalTabIndex
        );
      }

      if (expanded || isFocusableVisible(root, description, item)) {
        restoreOriginalTabIndex(item);
      } else {
        item.setAttribute("tabindex", "-1");
      }
    });
  }

  function setExpandedState(root, expanded) {
    const description = root.querySelector(SELECTORS.description);
    const toggle = root.querySelector(SELECTORS.toggle);
    const toggleText = root.querySelector(SELECTORS.toggleText);

    if (!description || !toggle || !toggleText) return;

    const label = expanded
      ? toggle.dataset.labelExpanded || "Collapse"
      : toggle.dataset.labelCollapsed || "Read More";

    root.classList.toggle("is-expanded", expanded);
    toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    toggleText.textContent = label;
    setDescriptionFocusables(root, description, expanded);
  }

  function resetIntro(root) {
    const description = root.querySelector(SELECTORS.description);
    const toggle = root.querySelector(SELECTORS.toggle);

    if (!description || !toggle) return;

    root.classList.remove("is-collapsible", "is-expanded");
    toggle.hidden = true;
    toggle.setAttribute("aria-expanded", "false");
    setDescriptionFocusables(root, description, true);
  }

  function shouldCollapseDescription(description) {
    const lineHeight = getLineHeight(description);
    if (!lineHeight) return false;

    return description.scrollHeight > lineHeight * COLLAPSED_LINES + OVERFLOW_TOLERANCE;
  }

  function getMeasurementSignature(root, description) {
    const computed = window.getComputedStyle(description);
    const width = Math.round(root.getBoundingClientRect().width * 10) / 10;

    return [
      width,
      computed.fontFamily,
      computed.fontSize,
      computed.lineHeight,
      computed.letterSpacing,
      computed.wordSpacing
    ].join("|");
  }

function refreshIntro(root) {
  const description = root.querySelector(SELECTORS.description);
  const toggle = root.querySelector(SELECTORS.toggle);

  if (!description || !toggle) return;

  const wasExpanded = root.classList.contains("is-expanded");

  /*
    Pending CSS already reserves the final Read More space so the
    first painted text wraps exactly like the collapsed state.

    Remove pending before measuring the natural description height,
    otherwise that temporary reserve could make a short description
    appear longer than it really is.

    This refresh runs synchronously inside one animation frame, so the
    temporary unreserved measurement state is not painted to screen.
  */
  root.classList.remove("is-pending");

  resetIntro(root);

  if (shouldCollapseDescription(description)) {
    root.classList.add("is-collapsible");
    toggle.hidden = false;
    setExpandedState(root, wasExpanded);
  }

  root.dataset.collectionIntroReady = "true";
  root.__collectionIntroSignature = getMeasurementSignature(root, description);
}

  function scheduleRefresh(root) {
    if (root.__collectionIntroFrame) {
      window.cancelAnimationFrame(root.__collectionIntroFrame);
    }

    root.__collectionIntroFrame = window.requestAnimationFrame(function () {
      refreshIntro(root);
      root.__collectionIntroFrame = null;
    });
  }

  function observeIntro(root, description) {
    if ("ResizeObserver" in window) {
      const observer = new ResizeObserver(function () {
        const signature = getMeasurementSignature(root, description);

        if (
          root.__collectionIntroSignature &&
          signature !== root.__collectionIntroSignature
        ) {
          root.__collectionIntroSignature = signature;
          scheduleRefresh(root);
        } else if (!root.__collectionIntroSignature) {
          root.__collectionIntroSignature = signature;
        }
      });

      observer.observe(root);
      root.__collectionIntroObserver = observer;
      return;
    }

    const resizeFallback = function () {
      scheduleRefresh(root);
    };

    window.addEventListener("resize", resizeFallback, { passive: true });
    root.__collectionIntroResizeFallback = resizeFallback;
  }

  function initCollectionIntro(root) {
    if (!root || root.dataset.collectionIntroInitialised === "true") return;

    const description = root.querySelector(SELECTORS.description);
    const toggle = root.querySelector(SELECTORS.toggle);

    if (!description || !toggle) {
      root.classList.remove("is-pending");
      return;
    }

    root.dataset.collectionIntroInitialised = "true";

    toggle.addEventListener("click", function () {
      setExpandedState(root, !root.classList.contains("is-expanded"));
    });

    observeIntro(root, description);
    scheduleRefresh(root);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        scheduleRefresh(root);
      });
    }
  }

  function bootCollectionIntros(scope) {
    const container = scope || document;
    container.querySelectorAll(SELECTORS.root).forEach(initCollectionIntro);
  }

  function destroyCollectionIntros(scope) {
    const container = scope || document;

    container.querySelectorAll(SELECTORS.root).forEach(function (root) {
      if (root.__collectionIntroObserver) {
        root.__collectionIntroObserver.disconnect();
      }

      if (root.__collectionIntroResizeFallback) {
        window.removeEventListener("resize", root.__collectionIntroResizeFallback);
      }

      if (root.__collectionIntroFrame) {
        window.cancelAnimationFrame(root.__collectionIntroFrame);
      }
    });
  }

  try {
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        function () {
          bootCollectionIntros(document);
        },
        { once: true }
      );
    } else {
      bootCollectionIntros(document);
    }

    document.addEventListener("shopify:section:load", function (event) {
      bootCollectionIntros(event.target);
    });

    document.addEventListener("shopify:section:unload", function (event) {
      destroyCollectionIntros(event.target);
    });
  } catch (error) {
    document.documentElement.classList.add("collection-banner-v2-compact-js-failed");
    window.setTimeout(function () {
      throw error;
    }, 0);
  }
})();




/* ========================================================================== 
   Blink Box - Recommendation analytics
   Markup source: snippets/recommendations.liquid
========================================================================== */
(function () {
  'use strict';

  if (window.__blinkboxRecommendationAnalyticsLoaded) return;
  window.__blinkboxRecommendationAnalyticsLoaded = true;

  const COMPONENT = 'product-recommendations[data-blinkbox-analytics="recommendations"]';
  const ITEM = '[data-recommendation-item]';
  const states = new WeakMap();
  const initialWatchers = new WeakMap();

  function buildPayload(component, item, action) {
    const position = Number(item.dataset.recommendationPosition || 0);
    const price = Number(item.dataset.recommendedPrice || 0) / 100;
    const payload = {
      placement: component.dataset.recommendationPlacement || '',
      recommendation_intent: component.dataset.recommendationIntent || '',
      source_product_id: component.dataset.sourceProductId || '',
      source_product_name: component.dataset.sourceProductName || '',
      source_product_handle: component.dataset.sourceProductHandle || '',
      recommended_product_id: item.dataset.recommendedProductId || '',
      recommended_product_name: item.dataset.recommendedProductName || '',
      recommended_product_handle: item.dataset.recommendedProductHandle || '',
      recommended_product_vendor: item.dataset.recommendedProductVendor || '',
      recommended_variant_id: item.dataset.recommendedVariantId || '',
      recommended_price: price,
      currency: component.dataset.currency || '',
      recommendation_position: position
    };

    if (action) payload.recommendation_action = action;
    return payload;
  }

  function buildGoogleParams(component, item, action) {
    const position = Number(item.dataset.recommendationPosition || 0);
    const price = Number(item.dataset.recommendedPrice || 0) / 100;
    const placement = component.dataset.recommendationPlacement || 'recommendations';
    const intent = component.dataset.recommendationIntent || 'related';
    const sourceId = component.dataset.sourceProductId || '';
    const sourceName = component.dataset.sourceProductName || '';
    const itemListId = `${placement}:${intent}:${sourceId}`;
    const itemListName = `${placement}:${intent}:${sourceName}`;
    const params = {
      item_list_id: itemListId,
      item_list_name: itemListName,
      recommendation_placement: placement,
      recommendation_intent: intent,
      source_product_id: sourceId,
      source_product_name: sourceName,
      source_product_handle: component.dataset.sourceProductHandle || '',
      recommendation_position: position,
      currency: component.dataset.currency || '',
      items: [{
        item_id: item.dataset.recommendedProductId || '',
        item_name: item.dataset.recommendedProductName || '',
        item_brand: item.dataset.recommendedProductVendor || '',
        item_variant: item.dataset.recommendedVariantId || '',
        item_list_id: itemListId,
        item_list_name: itemListName,
        index: Math.max(position - 1, 0),
        price
      }]
    };

    if (action) params.recommendation_action = action;
    return params;
  }

  function publishShopifyEvent(name, payload) {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    try {
      const result = window.Shopify.analytics.publish(name, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (error) {
      // Analytics must never interrupt shopping behaviour.
    }
  }

  function sendGoogleEvent(name, params) {
    if (typeof window.gtag !== 'function') return;

    try {
      window.gtag('event', name, params);
    } catch (error) {
      // Google measurement is optional.
    }
  }

  function clearViewTimer(state, item) {
    const timer = state.viewTimers.get(item);
    if (!timer) return;
    window.clearTimeout(timer);
    state.viewTimers.delete(item);
  }

  function trackView(component, item, state) {
    if (state.viewedItems.has(item)) return;

    clearViewTimer(state, item);
    state.viewedItems.add(item);
    state.observer?.unobserve(item);

    publishShopifyEvent(
      'blinkbox_recommendation_view',
      buildPayload(component, item)
    );

    sendGoogleEvent(
      'view_item_list',
      buildGoogleParams(component, item)
    );
  }

  function trackClick(component, item, state, action) {
    trackView(component, item, state);

    publishShopifyEvent(
      'blinkbox_recommendation_click',
      buildPayload(component, item, action)
    );

    sendGoogleEvent(
      'select_item',
      buildGoogleParams(component, item, action)
    );
  }

  function getAction(control) {
    if (control.matches('button[name="add"]')) return 'add_to_cart';
    if (control.matches('.js-quick-add')) return 'quick_add';
    if (control.matches('a[href]')) return 'product_click';
    return null;
  }

  function createState(component) {
    const state = {
      viewedItems: new WeakSet(),
      observedItems: new WeakSet(),
      viewTimers: new Map(),
      observer: null
    };

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const item = entry.target;
          if (state.viewedItems.has(item)) return;

          if (entry.intersectionRatio >= 0.6) {
            if (state.viewTimers.has(item)) return;

            state.viewTimers.set(item, window.setTimeout(() => {
              state.viewTimers.delete(item);
              trackView(component, item, state);
            }, 500));
          } else {
            clearViewTimer(state, item);
          }
        });
      }, { threshold: [0, 0.6] });
    }

    component.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const item = event.target.closest(ITEM);
      if (!item || !component.contains(item)) return;

      const control = event.target.closest('a[href], button');
      if (!control || !item.contains(control)) return;

      const action = getAction(control);
      if (action) trackClick(component, item, state, action);
    });

    component.dataset.blinkboxAnalyticsInitialised = 'true';
    states.set(component, state);

    return state;
  }

  function refreshAnalytics(component) {
    if (!component?.matches(COMPONENT)) return;

    const state = states.get(component) || createState(component);

    state.viewTimers.forEach((timer) => window.clearTimeout(timer));
    state.viewTimers.clear();
    state.observer?.disconnect();
    state.observedItems = new WeakSet();

    component.querySelectorAll(ITEM).forEach((item) => {
      if (state.observedItems.has(item)) return;

      state.observedItems.add(item);
      state.observer?.observe(item);
    });
  }

  function watchInitialLoad(component) {
    if (initialWatchers.has(component)) return;

    if (component.querySelector(ITEM)) {
      refreshAnalytics(component);
      return;
    }

    const watcher = new MutationObserver(() => {
      if (!component.querySelector(ITEM)) return;

      watcher.disconnect();
      initialWatchers.delete(component);
      refreshAnalytics(component);
    });

    initialWatchers.set(component, watcher);
    watcher.observe(component, {
      childList: true,
      subtree: true
    });
  }

  function scan(root) {
    const scope = root || document;

    if (scope instanceof Element && scope.matches(COMPONENT)) {
      watchInitialLoad(scope);
    }

    scope.querySelectorAll?.(COMPONENT).forEach(watchInitialLoad);
  }

  function patchComponent() {
    const ProductRecommendations =
      customElements.get('product-recommendations');

    if (!ProductRecommendations) return;

    const proto = ProductRecommendations.prototype;
    if (proto.__blinkboxAnalyticsPatched) return;

    const originalInit = proto.init;

    proto.init = async function (...args) {
      const result = await originalInit.apply(this, args);
      refreshAnalytics(this);
      return result;
    };

    Object.defineProperty(
      proto,
      '__blinkboxAnalyticsPatched',
      { value: true }
    );

    scan(document);
  }

  if (customElements.get('product-recommendations')) {
    patchComponent();
  } else {
    customElements
      .whenDefined('product-recommendations')
      .then(patchComponent);
  }

  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target);
  });
})();



/* Product pre-order message -> Product Info */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-product-info-link]');
  if (!link) return;

  event.preventDefault();

  const target = document.querySelector('[data-product-info-target]');
  if (!target) return;

  if (target.matches('details')) {
    target.open = true;
  } else if (target.matches('[role="tabpanel"]')) {
    const tab = document.querySelector('[aria-controls="' + target.id + '"]');
    if (tab) tab.click();
  }

  requestAnimationFrame(() => {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
});